import React, { useState } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { downloadFullBackupZip, downloadSingleCsv } from "../services/backupService.js";

export function BackupModal({ isOpen, onClose }) {
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [progress, setProgress] = useState("");
  const [successResult, setSuccessResult] = useState(null);
  const [error, setError] = useState(null);

  const handleDownloadFullZip = async () => {
    setIsBackingUp(true);
    setProgress("Initializing backup...");
    setError(null);
    setSuccessResult(null);

    try {
      const result = await downloadFullBackupZip((p) => {
        setProgress(p.message);
      });
      setSuccessResult(result);
    } catch (err) {
      console.error("Backup failed:", err);
      setError(err.message || "Failed to generate backup.");
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleDownloadCsv = async (collectionName) => {
    try {
      await downloadSingleCsv(collectionName);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="💾 Backup & Export Database" size="md">
      <div className="backup-modal-container" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        
        {/* Primary Action Card */}
        <Card className="backup-hero-card" style={{ padding: "1.25rem", background: "var(--bg-card-hover, rgba(56, 189, 248, 0.05))", border: "1px solid var(--accent-blue, #38bdf8)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
            <span style={{ fontSize: "1.75rem" }}>📦</span>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: "0 0 0.25rem 0", fontSize: "1rem", fontWeight: 600 }}>
                Complete Full-Fidelity Backup (.ZIP)
              </h4>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-secondary, #94a3b8)", lineHeight: 1.4 }}>
                Downloads all companies, parties, products, invoices, and inventory history. Contains both full <strong>JSON</strong> files (for database restore) and <strong>CSV</strong> spreadsheets (for Microsoft Excel).
              </p>
            </div>
          </div>

          <div style={{ marginTop: "1rem" }}>
            <Button
              variant="primary"
              onClick={handleDownloadFullZip}
              disabled={isBackingUp}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "0.75rem" }}
            >
              {isBackingUp ? (
                <>
                  <span className="spinner" style={{ display: "inline-block", width: "16px", height: "16px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  <span>{progress}</span>
                </>
              ) : (
                <>
                  <span>📥</span>
                  <span style={{ fontWeight: 600 }}>Download Backup Archive (.ZIP)</span>
                </>
              )}
            </Button>
          </div>

          {successResult && (
            <div style={{ marginTop: "0.75rem", padding: "0.75rem", borderRadius: "6px", background: "rgba(34, 197, 94, 0.15)", border: "1px solid #22c55e", fontSize: "0.85rem", color: "var(--text-primary, #f8fafc)" }}>
              <div style={{ fontWeight: 600, color: "#22c55e", marginBottom: "4px" }}>
                ✓ {successResult.fileName} downloaded!
              </div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, #94a3b8)" }}>
                Contains: {successResult.counts.invoices || 0} Invoices, {successResult.counts.items || 0} Items, {successResult.counts.customers || 0} Parties, {successResult.counts.companies || 0} Companies.
              </div>
            </div>
          )}

          {error && (
            <div style={{ marginTop: "0.75rem", padding: "0.75rem", borderRadius: "6px", background: "rgba(239, 68, 68, 0.15)", border: "1px solid #ef4444", fontSize: "0.85rem", color: "#ef4444" }}>
              ✗ {error}
            </div>
          )}
        </Card>

        {/* Quick CSV Accounting Exports */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <h5 style={{ margin: "0.25rem 0", fontSize: "0.85rem", color: "var(--text-secondary, #94a3b8)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Quick Excel / CSV Exports
          </h5>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.5rem" }}>
            <Button
              variant="secondary"
              className="btn-sm"
              onClick={() => handleDownloadCsv("invoices")}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
            >
              <span>🧾</span>
              <span>Invoices (.csv)</span>
            </Button>

            <Button
              variant="secondary"
              className="btn-sm"
              onClick={() => handleDownloadCsv("items")}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
            >
              <span>📦</span>
              <span>Items (.csv)</span>
            </Button>

            <Button
              variant="secondary"
              className="btn-sm"
              onClick={() => handleDownloadCsv("customers")}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
            >
              <span>👥</span>
              <span>Parties (.csv)</span>
            </Button>

            <Button
              variant="secondary"
              className="btn-sm"
              onClick={() => handleDownloadCsv("vendors")}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
            >
              <span>🏭</span>
              <span>Vendors (.csv)</span>
            </Button>
          </div>
        </div>

        {/* Informational Guidance Box */}
        <div style={{ padding: "0.85rem", borderRadius: "8px", background: "var(--bg-card, rgba(255,255,255,0.03))", border: "1px solid var(--border-color, rgba(255,255,255,0.08))", fontSize: "0.8rem", color: "var(--text-secondary, #94a3b8)", lineHeight: 1.5 }}>
          <div style={{ fontWeight: 600, color: "var(--text-primary, #f8fafc)", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
            <span>💡</span>
            <span>Where to store your backup:</span>
          </div>
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            <li>Save downloaded <code>.zip</code> files into your <strong>Google Drive</strong> folder or external USB storage.</li>
            <li>Send the CSV files to your tax accountant for easy GST & annual filing in Microsoft Excel.</li>
            <li>Automated weekly snapshots are also captured in your <strong>GitHub Actions</strong> repository artifacts.</li>
          </ul>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.5rem" }}>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>

      </div>
    </BottomSheet>
  );
}

export default BackupModal;
